<template>
  <div>
    <div class="container">
      <h5 class="mt-4 mb-4">
        {{ 'CONFIGURATION_MANAGEMENT' | translate('nav') }}
      </h5>
      <hr class="mt-4 mb-4">
      <div class="row">
        <div class="col-lg-5">
          <form v-on:submit.prevent="submitForm()">
            <div class="form-group">
              <label for="import_file" class="mb-4">
                {{ 'UPLOAD_CONFIGURATION_FILE' | translate }} :
              </label>
              <div class="form-row">
                <div class="col-lg-8">
                  <div class="custom-file">
                    <input type="file" class="custom-file-input" id="import_file" name="import_file"
                           v-on:change="selectFile" required>
                    <label class="custom-file-label" for="import_file">
                      {{ (file && file.name) ? file.name : 'UPLOAD_FILE' | translate }}
                    </label>
                  </div>
                </div>
                <div class="col">
                  <button type="submit" class="btn btn-primary" v-if="!submitting">{{ 'SUBMIT' | translate }}</button>
                  <img src="/static/loader-tail.svg" width="30" v-else>
                </div>
              </div>
            </div>
          </form>
          <div class="alert alert-success alert-dismissible mt-5" v-if="successMessage">
            {{ successMessage }}
            <button type="button" class="close" @click="dismissAlertSuccess()" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="alert alert-danger alert-dismissible mt-5" v-if="errorMessage">
            {{ errorMessage }}
            <button type="button" class="close" @click="dismissAlertError()" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
        <div class="col-lg-7">
          <div class="row">
            <div class="col-auto">
              <label class="mb-4">{{ 'OR_EDIT_DIRECTLY' | translate }}</label>
            </div>
            <div class="col text-right">
              <button type="button"
                      class="btn btn-sm btn-default"
                      v-on:click="getTemplateDownloadLink()"
              >{{ 'DOWNLOAD' | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-default"
                      v-on:click="resetFormConfiguration()"
                      :disabled="!cmLoaded">{{ 'LOAD_DEFAULT' | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-success"
                      v-on:click="saveConfig(cmContent)"
                      :title="(cmLintErrors.length > 0) ? 'FIX_EDITOR_ERRORS' : '' | translate('msg')"
                      :disabled="!cmLoaded || (cmLintErrors.length > 0)">{{ 'SAVE_CHANGES' | translate }}
              </button>
            </div>
          </div>
          <div class="cm-container">
            <codemirror v-model="cmContent" :options="cmOptions"></codemirror>
            <div class="cm-loader" v-if="!cmLoaded">
              <img src="/static/loader-tail.svg" width="30">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    import RestService from '../../service/RestService.js';
    import FormConfigService from '../../service/FormConfigService.js';
    import { codemirror } from 'vue-codemirror';
    import 'codemirror/lib/codemirror.css';
    import 'codemirror/addon/lint/lint.css';
    import 'codemirror/addon/lint/lint.js';
    import 'codemirror/addon/selection/active-line.js'; // additional option - active line
    // here the codemirror js is included to access the linting callbacks explicitly to get linting errors
    import CodeMirror from 'codemirror/lib/codemirror.js';
    /* json-specific */
    import jsonlint from 'jsonlint-mod';
    import 'codemirror/mode/javascript/javascript.js'; // language highlighting - js
    import 'codemirror/addon/lint/json-lint.js'; // json lint

    export default {
      components: {
        codemirror
      },
      data: function () {
        return {
          rest: new RestService(),
          submitting: false,
          file: null,
          selectedConfig: null,
          cmContent: '',
          cmLintErrors: [],
          cmOptions: {
            line: true,
            lineNumbers: true,
            mode: 'application/json',
            styleActiveLine: true,
            tabSize: 2,
            viewportMargin: Infinity,
            gutters: ['CodeMirror-lint-markers'],
            lint: {
              getAnnotations: CodeMirror.lint.json,
              onUpdateLinting: (lintErrors) => {
                this.cmLintErrors = lintErrors;
              }
            }
          },
          cmLoaded: true,
          successMessage: null,
          errorMessage: null
        }
      },
      beforeMount: function () {
        this.init();
        window.jsonlint = jsonlint;
      },
      methods: {
        init: function () {
          this.loadFormConfiguration();
        },
        submitForm: function () {
          this.submitting = true;
          let reader = new FileReader();
          let vm = this;
          reader.onload = function(e) {
            let fileContent = reader.result;
            vm.saveConfig(fileContent);
            vm.file = null;
            vm.$refs.inputFile.value = '';
          }
          reader.onerror = function(e) {
            vm.$log.error('Failed reading file: ' + e.message);
          }
          reader.readAsText(this.file);
        },
        saveConfig: function(config) {
          this.cmLoaded = false;
          var vm = this;
          this.rest.uploadCustomerConfigFile(config)
            .then((response) => {
              FormConfigService.updateFormConfig('CUSTOMER', config);
              vm.selectedConfig = config;
              vm.cmContent = config;
              vm.submitting = false;
              setTimeout(() => {
                vm.cmLoaded = true;
                vm.setSuccessMessage(this.$options.filters.translate('CONFIGURATION_SAVED', 'msg'));
              }, 300);
            }).catch((err) => {
              let errorMessage = '';
              // 422 Unprocessable Entity if there are any validation errors
              if (err.response.status === 422) {
                errorMessage = err.response.data;
              } else if (typeof err.response !== 'undefined') {
                this.$log.error(err.response.data);
              } else {
                this.$log.error(err);
              }
              vm.submitting = false;
              vm.cmLoaded = true;
              vm.setErrorMessage(this.$options.filters.translate('ERROR_SAVING_CONFIGURATION', 'msg') + '\n' + errorMessage);
            });
        },
        selectFile: function (evt) {
          this.file = evt.target.files[0];
        },
        getTemplateDownloadLink: function() {
          const url = window.URL.createObjectURL(new Blob([this.selectedConfig]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `customer.json`);
          link.click();
          window.URL.revokeObjectURL(url);
        },
        loadFormConfiguration: function() {
          this.cmLoaded = false;
          setTimeout(async () => { // giving the browser chance to render the loader before the page freezes
            this.selectedConfig = await FormConfigService.getFormConfig('CUSTOMER');
            this.cmContent = this.selectedConfig || ''; // the page freezes here due to rendering 1000+ rows in the editor
            this.cmLoaded = true;
          }, 100);
        },
        resetFormConfiguration: function() {
          this.cmLoaded = false;
          this.rest.getDefaultCustomerConfigForm()
            .then((response) => {
              let config = JSON.stringify(response.data, null, '\t');
              this.selectedConfig = config;
              this.cmContent = config;
              setTimeout(() => {
                this.cmLoaded = true;
                this.setSuccessMessage(this.$options.filters.translate('LOADED_DEFAULT_CONFIGURATION', 'msg') +
                ' (' + this.$options.filters.translate('CUSTOMER') + ')');
              }, 300);
            }).catch((err) => {
              if (typeof err.response !== 'undefined') {
                this.$log.error(err.response.data);
              } else {
                this.$log.error(err);
              }
              this.cmLoaded = true;
              this.setErrorMessage(this.$options.filters.translate('ERROR_LOADING_DEFAULT_CONFIGURATION', 'msg'));
            });
        },
        setSuccessMessage: function(message) {
          this.errorMessage = null;
          this.successMessage = message;
          const vm = this;
          setTimeout(function() {
            vm.dismissAlertSuccess();
          }, 3000);
        },
        setErrorMessage: function(message) {
          this.successMessage = null;
          this.errorMessage = message;
        },
        dismissAlertSuccess: function() {
          this.successMessage = null;
        },
        dismissAlertError: function() {
          this.errorMessage = null;
        }
      }
    }
</script>

<style>
  .CodeMirror {
    height: calc(100vh - 300px) !important;
    border: 1px solid #eee;
  }
  .cm-container {
    position: relative;
    overflow: hidden;
  }
</style>



// WEBPACK FOOTER //
// src/components/admin/CustomerConfigurationManagement.vue